// ExplorationModule.js - Version améliorée avec gestion complète des notes
import React, { useState, useEffect, useCallback } from 'react';
import { 
  CircularProgress, 
  Alert, 
  Chip, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  BookOpen,
  CheckCircle,
  ArrowRight,
  Upload,
  File,
  Lightbulb,
  TrendingUp,
  FileText,
  Target,
  Sparkles,
  ChevronDown,
  LayoutDashboard,
} from 'lucide-react';
import { doc, updateDoc, serverTimestamp, getDoc, collection, query, getDocs, addDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import KnowledgeBase from '../CP-KnowledgeDB/knowledgeBase';
import ConceptBasedQuiz from '../CP-Quiz/ConceptBasedQuiz';
import { askGPT } from '../components/OpenAIService';
import { MathJaxContext } from 'better-react-mathjax';

// Importer les sous-composants
import { NoteCard, NoteDetails, EmptyNotesList } from './ExplorationNotes';
import SynthesisList from '../CP-Exploration/SynthesisList';
import SynthesisViewer from '../CP-Exploration/SynthesisViewer';
import SynthesisFirebaseService from '../CP-Exploration/SynthesisFirebaseService';
import ExplorationNavigationCards from '../components/ExplorationNavigationCards';

// Hook pour détecter la taille de l'écran
const useScreenSize = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile };
};

const ExplorationModule = ({
  userId,
  currentCompetence,
  onSelectCompetence,
  userObjectives,
  onNavigateToStep,
  navigate,
  step
}) => {
  // Hook pour détecter mobile
  const { isMobile } = useScreenSize();

  // États principaux
  const [currentPhase, setCurrentPhase] = useState('overview');
  const [explorationStats, setExplorationStats] = useState({
    documentsCount: 0,
    conceptsCount: 0,
    notesCount: 0,
    lastActivity: null
  });
  
  // États pour le chargement et les erreurs
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour l'UI
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [selectedViewMode, setSelectedViewMode] = useState('overview');
  
  // États pour le suivi des activités
  const [activityLog, setActivityLog] = useState([]);
  const [hasExploredContent, setHasExploredContent] = useState(false);
  
  // États pour les notes
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  
  // États pour les synthèses
  const [syntheses, setSyntheses] = useState([]);
  const [loadingSyntheses, setLoadingSyntheses] = useState(false);
  const [selectedSynthesis, setSelectedSynthesis] = useState(null);
  
  // État pour la présentation de la base de connaissances
  const [knowledgeBasePresentation, setKnowledgeBasePresentation] = useState(null);
  const [loadingPresentation, setLoadingPresentation] = useState(false);

  // MathJax configuration
  const mathJaxConfig = {
    loader: { load: ['[tex]/html'] },
    tex: {
      packages: { '[+]': ['html'] },
      inlineMath: [['$', '$']],
      displayMath: [['$$', '$$']]
    },
    startup: {
      typeset: false
    }
  };

  // Chargement initial - démarrer directement en mode exploration
  useEffect(() => {
    const loadExplorationState = async () => {
      if (!currentCompetence || !userId) return;

      setInitialLoading(true);
      
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setExplorationStats({
          documentsCount: currentCompetence.documentsCount || 0,
          conceptsCount: currentCompetence.conceptsCount || 0,
          notesCount: currentCompetence.notesCount || 0,
          lastActivity: currentCompetence.lastExplorationActivity || null
        });
        
        const hasExistingKnowledge = currentCompetence.knowledgeBase || 
                                   currentCompetence.documentsCount > 0 ||
                                   currentCompetence.lastExplorationActivity;
        
        setHasExploredContent(hasExistingKnowledge);
        
        // Démarrer directement en mode exploration
        setCurrentPhase('exploring');
        
        console.log('État d\'exploration chargé pour:', currentCompetence.title);
        
      } catch (error) {
        console.error('Erreur lors du chargement de l\'état d\'exploration:', error);
        setError('Erreur lors du chargement de votre espace d\'exploration.');
        setCurrentPhase('exploring');
      } finally {
        setInitialLoading(false);
      }
    };

    loadExplorationState();
  }, [currentCompetence, userId]);

  // Charger les statistiques de la base de connaissances
  const loadKnowledgeBaseStats = useCallback(async () => {
    if (!userId || !currentCompetence?.id) return;
    
    try {
      // Compter le nombre réel de documents via l'API
      const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
      const { makeAuthenticatedRequest } = require('../components/APIService');
      
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/getUserDocuments?objectiveId=${currentCompetence.id}`,
        { method: 'GET' }
      );
      
      const data = await response.json();
      
      if (data.success && data.documents) {
        // Compter le nombre réel de documents au lieu de lire le compteur
        const realDocumentsCount = data.documents.length;
        
        console.log(`📊 Nombre réel de documents: ${realDocumentsCount}`);
        
        setExplorationStats(prev => ({
          ...prev,
          documentsCount: realDocumentsCount  // ← CALCUL RÉEL
        }));
      }
      
      // Charger aussi conceptsCount depuis Firebase (si nécessaire)
      const competenceRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      const competenceDoc = await getDoc(competenceRef);
      
      if (competenceDoc.exists()) {
        const firestoreData = competenceDoc.data();
        setExplorationStats(prev => ({
          ...prev,
          conceptsCount: firestoreData.conceptsCount || 0
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  }, [userId, currentCompetence]);

  // Charger les statistiques au montage
  useEffect(() => {
    if (userId && currentCompetence?.id) {
      loadKnowledgeBaseStats();
    }
  }, [userId, currentCompetence, loadKnowledgeBaseStats]);

  // Charger les notes avec gestion d'erreur de permissions
  const loadNotes = useCallback(async () => {
    if (!userId || !currentCompetence?.id) return;
    
    setLoadingNotes(true);
    try {
      const notesRef = collection(db, 'users', userId, 'competences', currentCompetence.id, 'notes');
      const q = query(notesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const loadedNotes = [];
      querySnapshot.forEach((doc) => {
        loadedNotes.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setNotes(loadedNotes);
      
      // Mettre à jour le compteur de notes
      setExplorationStats(prev => ({
        ...prev,
        notesCount: loadedNotes.length
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des notes:', error);
      
      if (error.code === 'permission-denied') {
        console.warn('Permissions insuffisantes pour les notes, initialisation...');
        setNotes([]);
        setExplorationStats(prev => ({
          ...prev,
          notesCount: 0
        }));
      }
    } finally {
      setLoadingNotes(false);
    }
  }, [userId, currentCompetence]);

  // Charger les notes au montage du composant
  useEffect(() => {
    if (userId && currentCompetence?.id) {
      loadNotes();
    }
  }, [userId, currentCompetence, loadNotes]);

  // Charger les synthèses
  useEffect(() => {
    const loadSyntheses = async () => {
      if (!userId || !currentCompetence?.id) return;
      
      setLoadingSyntheses(true);
      try {
        const result = await SynthesisFirebaseService.getUserSyntheses(userId, currentCompetence.id);
        if (result.success) {
          setSyntheses(result.syntheses || []);
        }
      } catch (error) {
        console.error('Erreur chargement synthèses:', error);
      } finally {
        setLoadingSyntheses(false);
      }
    };

    loadSyntheses();
  }, [userId, currentCompetence]);

  // Générer la présentation de la base de connaissances
  const generateKnowledgeBasePresentation = useCallback(async () => {
    if (!currentCompetence?.title || !userId) return;
    
    setLoadingPresentation(true);
    try {
      const docRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists() && docSnap.data().knowledgeBasePresentation) {
        setKnowledgeBasePresentation(docSnap.data().knowledgeBasePresentation);
      } else {
        const prompt = `Créer une présentation structurée de la base de connaissances pour l'objectif suivant : "${currentCompetence.title}".
        
        La présentation doit inclure :
        1. Une introduction expliquant l'importance de cette base de connaissances
        2. Les domaines clés à couvrir (3-5 domaines)
        3. Les bénéfices attendus de cette exploration
        
        Retourne le texte en 250 mots maximum, organisé en paragraphes clairs séparés par deux sauts de ligne.
        Utilise un ton professionnel mais accessible.`;

        const response = await askGPT(prompt);
        if (response) {
          setKnowledgeBasePresentation(response);
          
          await updateDoc(docRef, {
            knowledgeBasePresentation: response,
            knowledgeBasePresentationDate: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors de la génération de la présentation:', error);
    } finally {
      setLoadingPresentation(false);
    }
  }, [currentCompetence, userId]);

  // Générer la présentation au chargement de la vue d'ensemble
  useEffect(() => {
    if (userId && currentCompetence?.id && currentPhase === 'overview') {
      generateKnowledgeBasePresentation();
    }
  }, [userId, currentCompetence, currentPhase, generateKnowledgeBasePresentation]);

  // Fonction pour enregistrer l'activité d'exploration
  const logExplorationActivity = useCallback(async (activityType, details = {}) => {
    const activity = {
      type: activityType,
      timestamp: new Date().toISOString(),
      details,
      competenceId: currentCompetence?.id
    };
    
    setActivityLog(prev => [activity, ...prev.slice(0, 9)]);
    setHasExploredContent(true);
    
    if (currentCompetence?.id && userId) {
      try {
        await updateDoc(doc(db, 'users', userId, 'competences', currentCompetence.id), {
          lastExplorationActivity: serverTimestamp(),
          [`${activityType}Count`]: (currentCompetence[`${activityType}Count`] || 0) + 1
        });
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de l\'activité:', error);
      }
    }
  }, [currentCompetence, userId]);

  // AMÉLIORATION: Sauvegarder une recherche avec TOUS les détails (documents avec liens)
  const saveSearchAsNote = useCallback(async (searchQuery, searchResults, synthesis = null, documents = []) => {
    if (!userId || !currentCompetence?.id || !searchQuery) return;
    
    try {
      const notesRef = collection(db, 'users', userId, 'competences', currentCompetence.id, 'notes');
      
      console.log('📝 Documents reçus pour sauvegarde:', documents);
      
      // Extraire et formater les informations complètes des documents
      const linkedDocuments = documents.map(doc => {
        const docData = {
          id: doc.id || doc.documentId || doc._id,
          title: doc.title || doc.fileName || doc.name || 'Document sans titre',
          fileName: doc.fileName || doc.title || doc.name,
          fileUrl: doc.fileUrl || doc.url || doc.downloadURL || doc.path || null,
          score: doc.score || doc.similarity || doc.relevance || 0,
          excerpt: doc.excerpt || doc.content?.substring(0, 300) || doc.text?.substring(0, 300) || null,
          pageNumber: doc.pageNumber || doc.page || null
        };
        
        console.log('📄 Document traité:', docData);
        return docData;
      });
      
      console.log('✅ Documents liés finaux:', linkedDocuments);
      
      const noteData = {
        title: searchQuery,
        searchQuery: searchQuery,
        content: searchQuery,
        type: 'search',
        resultsCount: searchResults || 0,
        synthesis: synthesis ? {
          summary: typeof synthesis === 'string' ? synthesis : synthesis.summary || ''
        } : null,
        linkedDocuments: linkedDocuments, // Documents avec URLs
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('💾 Note à sauvegarder:', noteData);
      
      await addDoc(notesRef, noteData);
      await loadNotes();
      
      logExplorationActivity('notes', { 
        action: 'search_saved', 
        query: searchQuery,
        documentsCount: linkedDocuments.length
      });
      
      setError('✅ Recherche sauvegardée avec ' + linkedDocuments.length + ' document(s) !');
      setTimeout(() => setError(null), 3000);
      
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la note:', error);
      
      if (error.code === 'permission-denied') {
        setError('❌ Permissions insuffisantes pour sauvegarder les notes.');
      } else {
        setError('❌ Erreur lors de la sauvegarde de la note');
      }
      setTimeout(() => setError(null), 5000);
    }
  }, [userId, currentCompetence, loadNotes, logExplorationActivity]);

  // Supprimer une note
  const deleteNote = useCallback(async (noteId) => {
    if (!userId || !currentCompetence?.id || !noteId) return;
    
    try {
      const noteRef = doc(db, 'users', userId, 'competences', currentCompetence.id, 'notes', noteId);
      await deleteDoc(noteRef);
      await loadNotes();
      
      logExplorationActivity('notes', { 
        action: 'deleted', 
        noteId 
      });
      
      setError('✅ Note supprimée');
      setTimeout(() => setError(null), 2000);
    } catch (error) {
      console.error('Erreur lors de la suppression de la note:', error);
      setError('❌ Erreur lors de la suppression');
      setTimeout(() => setError(null), 3000);
    }
  }, [userId, currentCompetence, loadNotes, logExplorationActivity]);

  // Gestionnaire pour les événements de KnowledgeBase
  const handleKnowledgeBaseActivity = useCallback((activity) => {
    switch (activity.type) {
      case 'document_uploaded':
        logExplorationActivity('documents', { 
          action: 'upload', 
          documentName: activity.documentName 
        });
        // Rafraîchir les statistiques depuis la base de données
        loadKnowledgeBaseStats();
        break;
      case 'document_deleted':
        logExplorationActivity('documents', { 
          action: 'delete', 
          documentId: activity.documentId 
        });
        // Rafraîchir les statistiques depuis la base de données
        loadKnowledgeBaseStats();
        break;
      case 'documents_loaded':
        // Rafraîchir les statistiques quand les documents sont chargés
        loadKnowledgeBaseStats();
        break;
      case 'concept_explored':
        logExplorationActivity('concepts', { 
          conceptName: activity.conceptName 
        });
        break;
      case 'save_search_as_note':
        // AMÉLIORATION: S'assurer que tous les documents sont transmis avec leurs URLs
        if (activity.query) {
          saveSearchAsNote(
            activity.query, 
            activity.resultsCount, 
            activity.synthesis,
            activity.documents || [] // Documents complets avec URLs
          );
        }
        break;
      default:
        break;
    }
  }, [logExplorationActivity, saveSearchAsNote, loadKnowledgeBaseStats]);


  // Fonction pour continuer vers l'étape suivante
  const handleContinueToNextStep = () => {
    setSaveDialogOpen(false);
    onNavigateToStep('strategy');
  };

  // Fonction pour convertir le markdown en HTML
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    const blocks = text.split('\n\n');
    
    return blocks.map((block, index) => {
      let content = block;
      
      if (content.startsWith('###')) {
        content = content.replace(/^###\s*/, '');
        return (
          <h3 key={index} className="text-lg font-bold text-gray-800 mb-3 mt-4">
            {content}
          </h3>
        );
      }
      
      if (content.startsWith('##')) {
        content = content.replace(/^##\s*/, '');
        return (
          <h2 key={index} className="text-xl font-bold text-gray-800 mb-3 mt-4">
            {content}
          </h2>
        );
      }
      
      content = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      content = content.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      
      return (
        <p 
          key={index} 
          className="mb-4 text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    });
  };

  // Rendu de la section Synthèses
  const renderSynthesesSection = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {loadingSyntheses ? (
          <div className="flex justify-center py-12">
            <CircularProgress className="text-indigo-600" />
          </div>
        ) : selectedSynthesis ? (
          <SynthesisViewer
            synthesis={selectedSynthesis}
            onBack={() => setSelectedSynthesis(null)}
          />
        ) : (
          <SynthesisList
            syntheses={syntheses}
            onSelectSynthesis={setSelectedSynthesis}
            userId={userId}
            competenceId={currentCompetence?.id}
          />
        )}
      </div>
    </div>
  );

  // AMÉLIORATION: Rendu de la section Notes avec les nouveaux composants ET MathJaxContext
  const renderNotesSection = () => (
    <MathJaxContext config={mathJaxConfig}>
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <FileText className="h-6 w-6 mr-2 text-green-600" />
            Vos recherches sauvegardées ({notes.length})
          </h3>
          
          {loadingNotes ? (
            <div className="flex justify-center py-12">
              <CircularProgress className="text-green-600" />
            </div>
          ) : notes.length > 0 ? (
            <div className="space-y-4">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onClick={() => {
                    setSelectedNote(note);
                    setShowNoteDialog(true);
                  }}
                  onDelete={() => deleteNote(note.id)}
                />
              ))}
            </div>
          ) : (
            <EmptyNotesList />
          )}
        </div>
      </div>
    </MathJaxContext>
  );

  // Rendu de la section Vue d'ensemble
  const renderOverviewSection = () => (
    <div className="space-y-6">
      {/* Objectif mis en valeur */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Target className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre objectif</h3>
            <p className="text-gray-700 text-base leading-relaxed">{currentCompetence?.title}</p>
          </div>
        </div>
      </div>

      {/* Présentation de la base de connaissances */}
      {knowledgeBasePresentation && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center">
              <BookOpen className="h-6 w-6 mr-2 text-indigo-600" />
              Présentation de votre base de connaissances
            </h3>
            {loadingPresentation && (
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-indigo-400 border-t-indigo-200" />
            )}
          </div>
          <div className="prose prose-indigo max-w-none">
            {renderMarkdown(knowledgeBasePresentation)}
          </div>
        </div>
      )}

      {/* Cartes de navigation vers les fonctionnalités principales */}
      <ExplorationNavigationCards
        onNavigateToDocuments={() => setSelectedViewMode('documents')}
        onNavigateToSyntheses={() => setSelectedViewMode('syntheses')}
        onNavigateToNotes={() => setSelectedViewMode('notes')}
        onNavigateToQuiz={() => setSelectedViewMode('quiz')}
        stats={{
          documentsCount: explorationStats.documentsCount,
          synthesesCount: syntheses.length,
          notesCount: explorationStats.notesCount,
          quizCount: 0 // Les quiz n'ont pas de compteur spécifique
        }}
      />

      {/* Statistiques */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
        <div className="flex items-center mb-4">
          <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-800">Votre progression</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 text-center border border-blue-100">
            <div className="text-3xl font-bold text-blue-600 mb-1">
              {explorationStats.documentsCount}
            </div>
            <div className="text-sm text-gray-600 font-medium">Documents</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center border border-purple-100">
            <div className="text-3xl font-bold text-purple-600 mb-1">
              {explorationStats.conceptsCount}
            </div>
            <div className="text-sm text-gray-600 font-medium">Concepts</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center border border-green-100">
            <div className="text-3xl font-bold text-green-600 mb-1">
              {explorationStats.notesCount}
            </div>
            <div className="text-sm text-gray-600 font-medium">Notes</div>
          </div>
        </div>
      </div>

      {/* Recommandations pour bien démarrer */}
      {explorationStats.documentsCount === 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-200">
          <h4 className="font-semibold text-yellow-800 mb-4 flex items-center text-lg">
            <Lightbulb className="h-5 w-5 mr-2" />
            Pour bien démarrer
          </h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Téléchargez quelques documents pertinents à votre objectif
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Explorez les concepts automatiquement identifiés
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Sauvegardez vos recherches importantes dans vos notes
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">4</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Testez vos connaissances avec les quiz adaptatifs
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Rendu de l'exploration active
  const renderExploring = () => {
    return (
      <div className="space-y-4">
        {/* En-tête avec onglets de navigation */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          {/* Navigation responsive : Mobile vs Desktop */}
          {isMobile ? (
            /* MOBILE : Bouton "Vue d'ensemble" + Menu déroulant */
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedViewMode('overview')}
                className={`px-3 py-2.5 rounded-lg transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center ${
                  selectedViewMode === 'overview'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
                title="Vue d'ensemble"
              >
                <LayoutDashboard className="h-4 w-4" />
              </button>

              <div className="relative flex-1">
                <select
                  value={selectedViewMode === 'overview' ? '' : selectedViewMode}
                  onChange={(e) => e.target.value && setSelectedViewMode(e.target.value)}
                  className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Accès rapide</option>
                  <option value="syntheses">✨ Synthèses ({syntheses.length})</option>
                  <option value="documents">📄 Documents ({explorationStats.documentsCount})</option>
                  <option value="notes">📝 Notes ({notes.length})</option>
                  <option value="concepts">💡 Concepts</option>
                  <option value="quiz">✨ Quiz</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          ) : (
            /* DESKTOP : Les 5 boutons horizontaux */
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedViewMode('syntheses')}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedViewMode === 'syntheses'
                    ? 'bg-green-100 text-green-700 font-medium hover:border-green-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Sparkles className="h-4 w-4 flex-shrink-0" />
                <span>Synthèses</span>
                <span className="text-xs">({syntheses.length})</span>
              </button>

              <button
                onClick={() => setSelectedViewMode('documents')}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedViewMode === 'documents'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <File className="h-4 w-4 flex-shrink-0" />
                <span>Documents</span>
                <span className="text-xs">({explorationStats.documentsCount})</span>
              </button>

              <button
                onClick={() => setSelectedViewMode('notes')}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedViewMode === 'notes'
                    ? 'bg-purple-100 text-purple-700 font-medium'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span>Notes</span>
                <span className="text-xs">({notes.length})</span>
              </button>

              <button
                onClick={() => setSelectedViewMode('concepts')}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedViewMode === 'concepts'
                    ? 'bg-orange-100 text-orange-700 font-medium'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Lightbulb className="h-4 w-4 flex-shrink-0" />
                <span>Concepts</span>
              </button>

              <button
                onClick={() => setSelectedViewMode('quiz')}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  selectedViewMode === 'quiz'
                    ? 'bg-orange-100 text-orange-700 font-medium'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Sparkles className="h-4 w-4 flex-shrink-0" />
                <span>Quiz</span>
              </button>
            </div>
          )}
        </div>

        {/* Contenu selon le mode sélectionné */}
        {selectedViewMode === 'overview' ? (
          renderOverviewSection()
        ) : selectedViewMode === 'syntheses' ? (
          renderSynthesesSection()
        ) : selectedViewMode === 'notes' ? (
          renderNotesSection()
        ) : selectedViewMode === 'quiz' ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <ConceptBasedQuiz
              userId={userId}
              competence={currentCompetence}
              onQuizComplete={() => {
                logExplorationActivity('quiz', { completed: true });
              }}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <KnowledgeBase
              userId={userId}
              competence={currentCompetence}
              initialViewMode={selectedViewMode}
              onActivity={handleKnowledgeBaseActivity}
              showHeader={false}
              embedded={true}
            />
          </div>
        )}

        {/* Journal d'activité récente */}
        {activityLog.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Activité récente
            </h4>
            <div className="space-y-2">
              {activityLog.slice(0, 5).map((activity, index) => {
                const getActivityDisplay = (act) => {
                  switch (act.type) {
                    case 'documents':
                      return {
                        icon: '📄',
                        text: act.details?.action === 'upload' 
                          ? 'Document ajouté' 
                          : 'Document traité',
                        detail: act.details?.documentName || ''
                      };
                    case 'concepts':
                      return {
                        icon: '💡',
                        text: 'Concept exploré',
                        detail: act.details?.conceptName || ''
                      };
                    case 'notes':
                      return {
                        icon: '📝',
                        text: act.details?.action === 'search_saved' 
                          ? 'Recherche sauvegardée' 
                          : 'Note modifiée',
                        detail: act.details?.query || ''
                      };
                    case 'quiz':
                      return {
                        icon: '✨',
                        text: 'Quiz complété',
                        detail: ''
                      };
                    case 'exploration_started':
                      return {
                        icon: '🚀',
                        text: 'Exploration démarrée',
                        detail: act.details?.viewMode || ''
                      };
                    default:
                      return {
                        icon: '📌',
                        text: 'Activité',
                        detail: ''
                      };
                  }
                };

                const display = getActivityDisplay(activity);
                
                return (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate">
                      {display.icon} {display.text}
                      {display.detail && ` : ${display.detail}`}
                    </span>
                    <span className="text-gray-400 ml-2 flex-shrink-0">
                      {new Date(activity.timestamp).toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bouton Continuer en bas */}
        <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={() => setSaveDialogOpen(true)}
            className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ArrowRight className="h-5 w-5 mr-2" />
            Continuer vers la stratégie
          </button>
        </div>
      </div>
    );
  };

  // Affichage pendant le chargement initial
  if (initialLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <CircularProgress className="text-blue-600" />
            <div className="ml-3">
              <p className="text-blue-700 font-medium">
                Préparation de votre espace d'exploration...
              </p>
              <p className="text-blue-600 text-sm">
                Chargement de votre base de connaissances...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rendu principal
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
      <div className="max-w-5xl mx-auto">
        {/* En-tête du module */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <BookOpen className="h-8 w-8 mr-3 text-teal-500" />
                Espace d'Exploration
                {hasExploredContent && (
                  <Chip 
                    label="Actif"
                    size="small"
                    sx={{ 
                      ml: 2,
                      backgroundColor: '#dcfce7', 
                      color: '#166534',
                      fontWeight: 600
                    }}
                  />
                )}
              </h2>
              <p className="text-gray-600 mt-2">
                Constituez et explorez votre base de connaissances personnalisée
              </p>
            </div>
            
            {/* Bouton Vue d'ensemble et Indicateur de progression */}
            <div className="flex items-center space-x-12">
              <button
                onClick={() => setSelectedViewMode('overview')}
                className="hidden md:flex items-center justify-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm whitespace-nowrap transition-colors font-medium"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Vue d'ensemble
              </button>
              
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${currentPhase === 'overview' ? 'bg-teal-500' : 'bg-gray-300'}`} />
                <div className={`w-3 h-3 rounded-full ${currentPhase === 'exploring' ? 'bg-teal-500' : 'bg-gray-300'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Affichage des erreurs */}
        {error && (
          <Alert 
            severity={error.includes('✅') ? 'success' : 'error'} 
            className="mb-6"
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Contenu - Affichage direct de l'exploration */}
        {renderExploring()}

        {/* Dialog de continuation */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className="flex items-center">
            <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
            Exploration productive !
          </DialogTitle>
          <DialogContent>
            <p className="mb-4">
              Vous avez enrichi votre base de connaissances pour votre objectif.
            </p>
            
            {hasExploredContent && (
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-green-800 text-sm font-medium mb-2">
                  Résumé de votre exploration :
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm text-green-700">
                  <div>Documents : {explorationStats.documentsCount}</div>
                  <div>Concepts : {explorationStats.conceptsCount}</div>
                  <div>Notes : {explorationStats.notesCount}</div>
                  <div>Activités : {activityLog.length}</div>
                </div>
              </div>
            )}
            
            <p>
              Ces connaissances enrichiront votre stratégie et vos actions. 
              Souhaitez-vous continuer vers l'étape suivante du parcours ?
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>
              Continuer l'exploration
            </Button>
            <Button 
              onClick={handleContinueToNextStep}
              variant="contained"
              className="bg-teal-600 hover:bg-teal-700"
              endIcon={<ArrowRight className="h-4 w-4" />}
            >
              Retour à la Stratégie
            </Button>
          </DialogActions>
        </Dialog>

        {/* AMÉLIORATION: Dialog pour afficher le détail d'une note avec MathJaxContext */}
        <MathJaxContext config={mathJaxConfig}>
          <Dialog
            open={showNoteDialog}
            onClose={() => {
              setShowNoteDialog(false);
              setSelectedNote(null);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle className="flex items-center justify-between border-b border-gray-200 pb-3">
              <span className="flex items-center text-lg font-semibold">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                {selectedNote?.title}
              </span>
            </DialogTitle>
            <DialogContent className="mt-4">
              {selectedNote && <NoteDetails note={selectedNote} />}
            </DialogContent>
            <DialogActions className="border-t border-gray-200 pt-3">
              <Button 
                onClick={() => {
                  setShowNoteDialog(false);
                  setSelectedNote(null);
                }}
                variant="contained"
              >
                Fermer
              </Button>
            </DialogActions>
          </Dialog>
        </MathJaxContext>
      </div>
    </div>
  );
};

export default ExplorationModule;